import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { AccessDeniedNotice } from '../../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../../shared/RetryableError';
import { jobRoleGateway } from '../../../job-roles';
import { isForbidden, jobRoleErrorMessage } from '../../../job-roles/jobRoleErrors';
import type { EmployeeJobRoleAssignment } from '../../../job-roles/types';
import { formatReportDate } from '../../../reports/formatting';
import { peopleGateway } from '../../index';
import type { EmployeeResponse, JobTitleOption } from '../../types';

type History = { kind: 'loading' } | { kind: 'ready'; items: EmployeeJobRoleAssignment[] } | { kind: 'forbidden' } | { kind: 'error'; message: string };

/**
 * Job Role Library: a small READ-ONLY view of the employee's current job role and its assignment history. Editing stays where it was
 * (the Job Title field in Work Details). A job role is what the person does, not what they can do in EPN, and nothing here shows or
 * changes pay. History is the server's append-only record: an employee who has never had a role change has none yet, and this
 * says so rather than inventing one.
 */
export function EmployeeRoleSection({ employee }: { employee: EmployeeResponse }) {
  const uid = useId();
  const [titles, setTitles] = useState<JobTitleOption[]>([]);
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState<History>({ kind: 'loading' });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    peopleGateway.listJobTitles().then((t) => { if (!cancelled) setTitles(t); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [employee.jobTitleId]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHistory({ kind: 'loading' });
    jobRoleGateway
      .listEmployeeRoleAssignments(employee.id, controller.signal)
      .then((items) => { if (!controller.signal.aborted) setHistory({ kind: 'ready', items }); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setHistory(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: jobRoleErrorMessage(err, 'Could not load the role history.') });
      });
    return () => controller.abort();
  }, [open, employee.id, employee.jobTitleId, nonce]);

  const currentTitle = employee.jobTitleId ? (titles.find((t) => t.id === employee.jobTitleId)?.name ?? null) : null;
  const panelId = `${uid}-history`;

  return (
    <section className="profile-section" aria-labelledby={`${uid}-title`} data-testid="employee-role-section">
      <h3 className="profile-section-title" id={`${uid}-title`}>Job role</h3>
      {employee.jobTitleId ? (
        <p>
          Current role:{' '}
          <strong>{currentTitle ?? 'A role that is no longer active'}</strong>{' '}
          <Link to={`/people/roles/${employee.jobTitleId}`}>View role</Link>
        </p>
      ) : (
        <p>Current role: <span className="muted">Role not assigned</span></p>
      )}
      <p className="muted">
        This is the job role (what this person does), not system access. Change it with Job Title above; each change is kept in the history.
      </p>
      <button
        type="button"
        className="link"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide role assignment history' : 'View role assignment history'}
      </button>
      <div id={panelId} hidden={!open}>
        {open && history.kind === 'loading' && <p className="muted" role="status">Loading role history{'…'}</p>}
        {open && history.kind === 'forbidden' && <AccessDeniedNotice requiredPermissionLabel="permission to view roles (roles.read)" />}
        {open && history.kind === 'error' && <RetryableError message={history.message} onRetry={() => setNonce((n) => n + 1)} />}
        {open && history.kind === 'ready' && history.items.length === 0 && (
          <p className="muted">No role changes have been recorded yet. History begins with the next role change.</p>
        )}
        {open && history.kind === 'ready' && history.items.length > 0 && (
          <table className="profile-history-table">
            <caption className="muted">Role assignment history, newest first</caption>
            <thead>
              <tr><th>Role</th><th>Code</th><th>Version</th><th>From</th><th>To</th><th>Reason</th></tr>
            </thead>
            <tbody>
              {history.items.map((a, i) => (
                <tr key={a.id || `${a.roleTitle}-${a.effectiveFrom}-${i}`}>
                  <td>{a.roleTitle}{a.roleStatus === 'Archived' ? ' (archived)' : ''}{a.effectiveTo === null ? ' (current)' : ''}</td>
                  <td>{a.roleCode ?? '—'}</td>
                  <td>{a.versionNumber != null ? `v${a.versionNumber}` : '—'}</td>
                  <td>{formatReportDate(a.effectiveFrom)}</td>
                  <td>{a.effectiveTo ? formatReportDate(a.effectiveTo) : '—'}</td>
                  <td>{a.reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
